import { useTheme } from "@/hooks/useTheme";
import { CustomRoute } from "@/interfaces";
import { cn, getContrastTextColor } from "@/lib/utils";
import { canView, canEdit } from "@/lib/permissionUtils";
import { PermissionsMap } from "@/lib/permissions";
import { getCurrentUserRouter } from "@/routers/CurrentRoutes";
import { useUserStore } from "@/stores/userStore";
import { checkIsApprover, checkIsApproverforFinance } from "@/services/billApprovalService";
import { ChevronDown } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router";

const UserMenu = () => {
  const role = useUserStore((state) => state.getRole());
  const permissions = useUserStore((state) => state.user?.permissions) as PermissionsMap | undefined;
  const currentCompany = useUserStore((state) => state.currentCompany);

  // Memoize routes to prevent infinite re-renders
  const user = useUserStore((state) => state.user);
  const routes = useMemo(() => getCurrentUserRouter(role, user), [role, user]);
  const pathname = useLocation().pathname;
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const { theme } = useTheme();
  const textColor = getContrastTextColor(theme.secondaryColor);
  const isNetSuiteCompany = ((currentCompany?.invoicingAccounting || "").toString().trim().toLowerCase() === "netsuite");

  const shouldHideBillingOption = (path?: string) => {
    if (!path) return false;

    // Features that should ONLY show for QuickBooks companies
    if (isNetSuiteCompany && (path.includes("recurring-invoices") || path === "/billing/stripe-payment")) {
      return true;
    }

    // Existing role-based hiding logic (only for non-NetSuite companies)
    if (!isNetSuiteCompany) {
      // if (user?.role === "Admin" && path === "/billing/vendor-bills") return true;
      if (user?.role === "Employee" && path === "/vendor-bills") return true;
      if (user?.role === "User" && (path === "/bills" || path === "/create-bill")) return true;
    }

    return false;
  };

  const shouldHideInvoicingOption = (menuName?: string, path?: string) => {
    if (!menuName) return false;

    // Hide "Ouickbooks menu" for Finance user if none of the company reuires QuickBooks
    if (
      (menuName === "QuickBooks") &&
      !user?.companies.some(obj => obj.invoicingAccounting === "QuickBooks") &&
      user?.role === "Finance"
    ) {
      return true;
    }

    // Hide "Netsuite menu" for Finance user if none of the company reuires NetSuite
    if (
      menuName === "NetSuite" &&
      !user?.companies.some(obj => obj.invoicingAccounting === "NetSuite") &&
      user?.role === "Finance"
    ) {
      return true;
    }


    if (
      (path === "/netsuite/approvals") &&
      currentCompany?.invoicingAccounting === "QuickBooks" &&
      user?.role === "Admin"
    ) {
      return true;
    }


    if (
      (path === "/quickbooks/approvals") &&
      currentCompany?.invoicingAccounting === "NetSuite" &&
      user?.role === "Admin"
    ) {
      return true;
    }

    return false;
  }

  // Check if current user is a bill approver — only relevant for Finance/Admin
  const [isApprover, setIsApprover] = useState<boolean | null>(null);
  useEffect(() => {
    if (user?.role === "Admin" || user?.role === "Employee") {
      checkIsApprover(currentCompany?.id)
        .then((res) => setIsApprover(res.isApprover))
        .catch(() => setIsApprover(false));
    } else if (user?.role === "Finance") {
      const assignedCompanies = user.companies.map((company) => company.id)
      if (assignedCompanies.length > 0) {
        checkIsApproverforFinance(assignedCompanies)
          .then((res) => setIsApprover(res.isApprover))
          .catch(() => setIsApprover(false));
      }
    }
  }, [user?.id, user?.role, currentCompany?.id]);

  // Keep the submenu open if the current pathname belongs to one of its children.
  // Otherwise close all submenus when navigating to a different top-level route.
  useEffect(() => {
    const newOpen: Record<string, boolean> = {};
    routes.forEach((r) => {
      if (r.submenu && r.submenu.length > 0) {
        const match = r.submenu.some((s) => {
          // Exact match or startsWith to support nested paths
          if (!s.path) return false;
          return pathname === s.path || pathname.startsWith(s.path + "/") || pathname.startsWith(s.path);
        });
        if (match) newOpen[r.menuName] = true;
      }
    });

    setOpenMenus(newOpen);
  }, [pathname, routes]);

  const toggleMenu = (key: string) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-2">
      {routes
        .filter((item: CustomRoute) => {
          // Don't show items marked as hideInMenu
          if (item.hideInMenu) return false;
          if (shouldHideBillingOption(item.path)) return false;
          if (shouldHideInvoicingOption(item.menuName, item.path)) return false;

          // If route has a permissionKey, check if user has at least read access
          // If no permissionKey, always show (e.g., Profile)
          if (item.permissionKey) {
            return canView(permissions, item.permissionKey);
          }

          return true;
        })
        .map((item: CustomRoute) => {
          const isActive = pathname === item.path;

          // If the route has a submenu, render a collapsible dropdown
          const Icon = item.icon as React.ElementType | undefined;
          if (item.submenu && item.submenu.length > 0) {
            const isOpen = !!openMenus[item.menuName];
            return (
              <div key={item.menuName}>
                <div
                  className={cn(
                    "flex items-center justify-between gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer",
                    isActive ? "bg-white/10" : "hover:bg-white/5",
                  )}
                  style={{ color: textColor }}
                  onClick={() => toggleMenu(item.menuName)}
                >
                  <div className="flex items-center gap-3">
                    {Icon ? <Icon className="h-5 w-5" /> : null}
                    <span>{item.menuName}</span>
                  </div>

                  <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen ? "rotate-180" : "rotate-0")} />
                </div>

                <div className={cn("mt-1 space-y-1 pl-8", isOpen ? "block" : "hidden")}>
                  {item.submenu
                    .filter((sub) => {
                      if (sub.hideInMenu) return false;
                      if (shouldHideBillingOption(sub.path)) return false;

                      if (
                        (sub.path === "/netsuite/approvals") &&
                        currentCompany?.invoicingAccounting === "QuickBooks" &&
                        user?.role === "Admin"
                      ) {
                        return false;
                      }


                      if (
                        (sub.path === "/quickbooks/approvals") &&
                        currentCompany?.invoicingAccounting === "NetSuite" &&
                        user?.role === "Admin"
                      ) {
                        return false;
                      }

                      if (currentCompany?.invoicingAccounting === "QuickBooks" && user?.role === "Admin" && sub.path === "/billing/create-bill") {
                        return false;
                      }

                      // Check permissionKey for submenus
                      if (sub.permissionKey && !canView(permissions, sub.permissionKey)) {
                        return false;
                      }

                      // Hide "Create Invoice" for Employee if permission level is only "read"
                      if (
                        user?.role === "Employee" &&
                        sub.path === "/my-invoices/create" &&
                        sub.permissionKey === "BillingInvoices" &&
                        !canEdit(permissions, sub.permissionKey)
                      ) {
                        return false;
                      }

                      // Hide "Create Bill" for Employee if permission level is only "read"
                      if (
                        user?.role === "Employee" &&
                        sub.path === "/vendor-bills/create" &&
                        sub.permissionKey === "BillingInvoices" &&
                        !canEdit(permissions, sub.permissionKey)
                      ) {
                        return false;
                      }

                      // Hide Vendor Bills Approvals from users who are not in bill approval config
                      if (
                        (sub.path === "/netsuite/vendor-bill-approvals" ||
                          sub.path === "/billing/vendor-bill-approvals" ||
                          sub.path === "/vendor-bill-approvals") &&
                        (user?.role === "Finance" || user?.role === "Admin" || user?.role === "Employee")
                      ) {
                        return isApprover === true;
                      }
                      return true;
                    })
                    .map((sub) => {
                      const subActive = pathname === sub.path;
                      return (
                        <Link
                          key={sub.menuName}
                          to={sub.path}
                          className={cn(
                            "flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors",
                            subActive ? "bg-white/10" : "hover:bg-white/5",
                          )}
                          style={{ color: textColor, opacity: subActive ? 1 : 0.7 }}
                        >
                          {sub.menuName}
                        </Link>
                      );
                    })}
                </div>
              </div>
            );
          }

          // Otherwise render a normal link
          return (
            <Link
              key={item.menuName}
              to={item.path}
              onClick={() => setOpenMenus({})}
              className={cn(
                "sidebar-nav-link flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors",
                isActive ? "bg-white/10" : "hover:bg-white/5",
              )}
              style={{ color: textColor }}
            >
              {Icon ? <Icon className="h-5 w-5" /> : null}
              {item.menuName}
            </Link>
          );
        })}
    </div>
  );
};

export default UserMenu;
